/**
 * 语义缩放（design §3.2、§3.3）：离得越近，显出的时间刻度越细。
 * - 星海（镜头离注视点超过 99 个单位）：每一圈标上年份，「在一起」那天和星轨尽头的「未完待续」，
 *   一条金色的纪念日射线从中心穿过每年的同一天（每年的同一天本来就在同一个方向上）。
 * - 一年（44 个单位以内，前几年的圈距窄，能看到好几圈）：螺线上画出月份刻度、标上月份；纪念日射线上标出「一周年」「两周年」；照片多的日子显出回忆标题。
 * - 一天（14 个单位以内）：星团里的照片显影（photos.ts），月份刻度淡下去，回忆标题还在。
 * 文字是 DOM 标签。月份、周年、回忆标题各有上限，只给在画面里、离镜头近的那几个分配节点，每帧只改它们，
 * 改之前和上一帧比一下，没变就不写（以前每帧给所有标签写一遍 transform）。
 * 屏幕上互相避让：先放回忆标题（照片多的优先），再放周年、月份，和已经放好的（包括年份）叠在一起的这一帧就不显示。
 */
import * as THREE from 'three';
import { SPIRAL, dayAngle, dayAnchor, dayRadius, daysInMonth, formatLocalDate, parseLocalDate, type LocalDate } from '@star-sea/shared';

/** 照片多的日子：日期、张数、回忆标题 */
export interface DayInfo {
  date: LocalDate;
  count: number;
  title: string | null;
}

export interface SemanticBuild {
  since: LocalDate;
  /** 螺线从哪天画到哪天 */
  first: LocalDate;
  end: LocalDate;
  hasPhotos: boolean;
  days: DayInfo[];
}

/**
 * 镜头距离的单位：螺线还是等距时的圈距（11）。这些距离是按标签疏密在屏幕上调出来的，
 * 换成对数螺线（ADR-0010）以后圈距随年份变（头一年约 5，第 10 年约 14），镜头距离照旧
 */
const G = 11;
/** 镜头离注视点多远算「一年」「一天」：之间平滑过渡 */
const YEAR_FAR = 9 * G;
const YEAR_NEAR = 4 * G;
const DAY_FAR = 14;
const DAY_NEAR = 6;
/** 一个层级要退回上一层时的镜头距离（Esc，见 Galaxy.stepBack） */
export const YEAR_VIEW = 4.4 * G;
export const DAY_LEVEL = DAY_FAR;
/** 照片至少这么多张的日子才显标题 */
export const TITLE_MIN = 3;
const CAP = { month: 24, anniv: 8, title: 12 } as const;
const NUMERALS = '一两三四五六七八九十';

/** 每种标签的字号和字距（em），估算它在屏幕上占多大，和 style.css 对应 */
const FONT = { year: [12, 0.12], since: [12, 0.12], tail: [12, 0.3], month: [11, 0.08], anniv: [12, 0.2], title: [13, 0.06] } as const;

interface Rect {
  x0: number;
  x1: number;
  y0: number;
  y1: number;
}

/** 文字居中放在 (x, y) 时大概占的矩形：汉字一个字号宽，数字和字母六成，加上字距和一点留白 */
function rectOf(text: string, kind: keyof typeof FONT, x: number, y: number): Rect {
  const [fs, ls] = FONT[kind];
  let w = 8;
  for (const ch of text) w += (ch.charCodeAt(0) < 0x2e80 ? 0.62 : 1) * fs + ls * fs;
  const h = fs * 1.5;
  return { x0: x - w / 2, x1: x + w / 2, y0: y - h / 2, y1: y + h / 2 };
}

const overlaps = (a: Rect, list: Rect[]) => list.some((b) => a.x0 < b.x1 && b.x0 < a.x1 && a.y0 < b.y1 && b.y0 < a.y1);

const smoothstep = (a: number, b: number, x: number) => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

/** 螺线上某一天往外（或往里）挪 out 个单位的位置 */
function along(date: LocalDate, since: LocalDate, out: number, lift = 0): THREE.Vector3 {
  const a = dayAnchor(date, since);
  const r = Math.hypot(a.x, a.z) || 1, k = (r + out) / r;
  return new THREE.Vector3(a.x * k, a.y + lift, a.z * k);
}

interface Item {
  key: string;
  text: string;
  pos: THREE.Vector3;
  /** 越大越优先 */
  weight: number;
}

/** 一种标签的一组 DOM 节点：最多 cap 个，同一个标签尽量一直用同一个节点 */
class LabelPool {
  private readonly parent: HTMLElement;
  private readonly cls: string;
  private readonly free: HTMLElement[] = [];
  private readonly used = new Map<string, HTMLElement>();
  private readonly last = new WeakMap<HTMLElement, { o: string; t: string }>();

  constructor(parent: HTMLElement, cls: string) {
    this.parent = parent;
    this.cls = cls;
  }

  show(items: { key: string; text: string; x: number; y: number; opacity: number }[]): void {
    const keep = new Set(items.map((i) => i.key));
    for (const [k, el] of this.used) {
      if (keep.has(k)) continue;
      el.hidden = true;
      this.used.delete(k);
      this.free.push(el);
    }
    for (const it of items) {
      let el = this.used.get(it.key);
      if (!el) {
        el = this.free.pop() ?? this.create();
        el.textContent = it.text;
        el.hidden = false;
        this.used.set(it.key, el);
      }
      write(el, this.last, it.opacity, it.x, it.y);
    }
  }

  clear(): void {
    this.show([]);
  }

  private create(): HTMLElement {
    const el = document.createElement('div');
    el.className = `label ${this.cls}`;
    this.parent.appendChild(el);
    return el;
  }
}

function write(el: HTMLElement, last: WeakMap<HTMLElement, { o: string; t: string }>, opacity: number, x: number, y: number): void {
  const o = opacity.toFixed(2), t = `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px) translate(-50%, -50%)`;
  const prev = last.get(el);
  if (prev?.o !== o) el.style.opacity = o;
  if (prev?.t !== t) el.style.transform = t;
  last.set(el, { o, t });
}

export class SemanticLayer {
  /** 月份刻度和纪念日射线，放进星海的场景 */
  readonly group = new THREE.Group();
  private readonly labelLayer: HTMLElement;
  private fixed: { el: HTMLElement; pos: THREE.Vector3; kind: 'year' | 'since' | 'tail' }[] = [];
  private readonly fixedLast = new WeakMap<HTMLElement, { o: string; t: string }>();
  private months: Item[] = [];
  private annivs: Item[] = [];
  private titles: Item[] = [];
  private readonly pools: Record<'month' | 'anniv' | 'title', LabelPool>;
  private ticks: THREE.LineSegments<THREE.BufferGeometry, THREE.LineBasicMaterial> | null = null;
  private ray: THREE.Line<THREE.BufferGeometry, THREE.LineBasicMaterial> | null = null;
  private since: LocalDate | null = null;
  private readonly v = new THREE.Vector3();
  /** 上一帧显示着的标签：避让时优先留下它们，免得两个挨着的标签来回闪 */
  private shown = new Set<string>();

  constructor(labelLayer: HTMLElement) {
    this.labelLayer = labelLayer;
    this.pools = { month: new LabelPool(labelLayer, 'label-month'), anniv: new LabelPool(labelLayer, 'label-anniv'), title: new LabelPool(labelLayer, 'label-title') };
  }

  rebuild(b: SemanticBuild): void {
    this.since = b.since;
    for (const f of this.fixed) f.el.remove();
    this.fixed = [];
    const fixed = (date: LocalDate, text: string, kind: 'year' | 'since' | 'tail') => {
      const el = document.createElement('div');
      el.className = `label label-${kind}`;
      el.textContent = text;
      this.labelLayer.appendChild(el);
      this.fixed.push({ el, pos: along(date, b.since, 3.2), kind });
    };
    const s = parseLocalDate(b.since);
    fixed(b.since, `在一起 · ${s.y}.${String(s.m).padStart(2, '0')}.${String(s.d).padStart(2, '0')}`, 'since');
    const y0 = parseLocalDate(b.first).y, y1 = parseLocalDate(b.end).y;
    for (let y = y0 + 1; y <= y1; y++) fixed(formatLocalDate(y, 1, 1), String(y), 'year');
    // 星轨的尽头（同原型）；还没有照片时不放
    if (b.hasPhotos) fixed(b.end, '未完待续', 'tail');

    // 月份刻度：每个月 1 号一道，横跨螺线；1 月那道长一些（年份标在那里）
    const tick: number[] = [];
    this.months = [];
    const f = parseLocalDate(b.first);
    for (let y = f.y, m = f.m + 1; ; m++) {
      if (m > 12) {
        y++;
        m = 1;
      }
      const date = formatLocalDate(y, m, 1);
      if (date > b.end) break;
      const half = m === 1 ? 1.4 : 0.7, a = along(date, b.since, -half), c = along(date, b.since, half);
      tick.push(a.x, a.y, a.z, c.x, c.y, c.z);
      if (m !== 1) this.months.push({ key: date, text: `${m} 月`, pos: along(date, b.since, 1.7), weight: 1 });
    }
    this.dispose();
    const tg = new THREE.BufferGeometry();
    tg.setAttribute('position', new THREE.BufferAttribute(new Float32Array(tick), 3));
    this.ticks = new THREE.LineSegments(tg, new THREE.LineBasicMaterial({ color: '#c6cdff', transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false }));
    this.ticks.frustumCulled = false;

    // 纪念日射线：沿着「在一起」那天的方向，从中心附近一直到螺线外
    const ang = dayAngle(b.since), rEnd = dayRadius(b.end, b.since) + 3, ray: number[] = [];
    for (let r = SPIRAL.r0 * 0.55; r <= rEnd; r += 1.5) ray.push(Math.sin(ang) * r, Math.sin(ang * 2 + r * 0.045) * SPIRAL.warp, -Math.cos(ang) * r);
    const rg = new THREE.BufferGeometry();
    rg.setAttribute('position', new THREE.BufferAttribute(new Float32Array(ray), 3));
    this.ray = new THREE.Line(rg, new THREE.LineBasicMaterial({ color: '#f3cf8b', transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false }));
    this.ray.frustumCulled = false;
    this.group.add(this.ticks, this.ray);

    // 每年的纪念日：标在射线内侧，免得和外侧的月份撞在一起
    this.annivs = [];
    for (let k = 1; ; k++) {
      const y = s.y + k, date = formatLocalDate(y, s.m, Math.min(s.d, daysInMonth(y, s.m)));
      if (date > b.end) break;
      this.annivs.push({ key: date, text: k <= 10 ? `${NUMERALS.charAt(k - 1)}周年` : `${k} 周年`, pos: along(date, b.since, -1.9), weight: 1 });
    }
    this.setDays(b.days);
  }

  /** 日子的照片数、标题变了（加了照片、改了回忆）：只换回忆标题 */
  setDays(days: DayInfo[]): void {
    if (!this.since) return;
    const since = this.since;
    this.titles = days
      .filter((d) => d.count >= TITLE_MIN)
      .map((d) => {
        const p = parseLocalDate(d.date);
        return { key: d.date, text: `${d.title ?? `${p.m} 月 ${p.d} 日`} · ${d.count} 张`, pos: along(d.date, since, 0, 1.5 + 0.12 * Math.sqrt(d.count)), weight: d.count };
      });
    this.pools.title.clear();
  }

  /** 每帧调：按镜头离注视点多远决定显到哪一层；k 是螺线、文字整体的可见度（入场、轮转时为 0） */
  update(o: { camera: THREE.PerspectiveCamera; target: THREE.Vector3; width: number; height: number; k: number }): { yearK: number; dayK: number } {
    const cam = o.camera, w = o.width, h = o.height;
    const camDist = cam.position.distanceTo(o.target);
    const yearK = smoothstep(YEAR_FAR, YEAR_NEAR, camDist), dayK = smoothstep(DAY_FAR, DAY_NEAR, camDist);
    if (this.ticks) this.ticks.material.opacity = 0.32 * yearK * (1 - 0.6 * dayK) * o.k;
    if (this.ray) this.ray.material.opacity = (0.14 + 0.1 * yearK) * (1 - 0.5 * dayK) * o.k;
    const project = (pos: THREE.Vector3) => {
      const v = this.v.copy(pos).project(cam);
      return v.z < 1 && v.z > -1 && Math.abs(v.x) < 1.05 && Math.abs(v.y) < 1.05 ? { x: (v.x * 0.5 + 0.5) * w, y: (-v.y * 0.5 + 0.5) * h } : null;
    };
    // 年份、在一起、未完待续：一直在，远的淡一些（以前的做法）；到了一天的层级，年份淡下去
    const placed: Rect[] = [];
    for (const l of this.fixed) {
      const s = project(l.pos), d = cam.position.distanceTo(l.pos);
      const fade = s ? THREE.MathUtils.clamp(1.6 - d / (camDist * 1.4), 0.25, 1) * (l.kind === 'year' ? 1 - 0.6 * dayK : 1) : 0;
      if (s) {
        write(l.el, this.fixedLast, fade, s.x, s.y);
        if (fade > 0.3) placed.push(rectOf(l.el.textContent ?? '', l.kind, s.x, s.y));
      } else if (this.fixedLast.get(l.el)?.o !== '0.00') write(l.el, this.fixedLast, 0, -999, -999);
    }
    const pick = (items: Item[], kind: 'month' | 'anniv' | 'title', cap: number, level: number, near: number) => {
      if (level < 0.02 || o.k < 0.02) return [];
      const cands: { key: string; text: string; x: number; y: number; opacity: number; score: number }[] = [];
      for (const it of items) {
        const d = cam.position.distanceTo(it.pos);
        const fade = level * THREE.MathUtils.clamp(1.5 - d / (camDist * 1.3), 0, 1) * (near > 0 ? smoothstep(near * 0.4, near, d) : 1);
        if (fade < 0.02) continue;
        const s = project(it.pos);
        const stay = this.shown.has(`${kind}:${it.key}`) ? 1.6 : 1;
        if (s) cands.push({ key: it.key, text: it.text, x: s.x, y: s.y, opacity: fade, score: (stay * fade * it.weight) / Math.max(1, d) });
      }
      cands.sort((a, b) => b.score - a.score);
      const out: typeof cands = [];
      for (const c of cands) {
        if (out.length >= cap) break;
        const r = rectOf(c.text, kind, c.x, c.y);
        if (overlaps(r, placed)) continue;
        placed.push(r);
        out.push(c);
        shown.add(`${kind}:${c.key}`);
      }
      return out;
    };
    const shown = new Set<string>();
    // 回忆标题最先放；离那个星团太近时（照片已经铺满画面）让开
    this.pools.title.show(pick(this.titles, 'title', CAP.title, yearK, 3));
    this.pools.anniv.show(pick(this.annivs, 'anniv', CAP.anniv, yearK, 0));
    this.pools.month.show(pick(this.months, 'month', CAP.month, yearK * (1 - 0.7 * dayK), 0));
    this.shown = shown;
    return { yearK, dayK };
  }

  private dispose(): void {
    for (const o of [this.ticks, this.ray]) {
      if (!o) continue;
      this.group.remove(o);
      o.geometry.dispose();
      o.material.dispose();
    }
    this.ticks = null;
    this.ray = null;
  }
}

